import { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useJsonLd, breadcrumbSchema, faqSchema } from "@/hooks/useJsonLd";
import { api } from "@/lib/api";
import {
  ArrowRight, Building2, Users, Shield, Award, BookOpen, CheckCircle2,
  GraduationCap, Layers, Briefcase, Mail, Phone,
} from "lucide-react";
import { toast } from "sonner";

const OFFERINGS = [
  {
    id: "osha",
    icon: Shield,
    title: "OSHA Safety Training",
    bullets: ["OSHA 30 Construction", "Confined space awareness", "Electrical safety NFPA 70E", "Fall protection"],
    track_link: null,
  },
  {
    id: "fundamentals",
    icon: BookOpen,
    title: "Fire Alarm Fundamentals",
    bullets: ["NFPA 72 system anatomy", "UL 864 panel essentials", "Detector + appliance theory", "Read shop drawings"],
    track_link: "/nicet-exam-prep",
  },
  {
    id: "s98",
    icon: Award,
    title: "FDNY S-98 Prep",
    bullets: ["Full curriculum + practice", "NYC Fire Code FC §907", "3 RCNY 901-01 paperwork", "Mock exam + remediation"],
    track_link: "/free-guide",
  },
  {
    id: "nicet",
    icon: GraduationCap,
    title: "NICET Level I-III Prep",
    bullets: ["Level I Foundation (140 hrs)", "Level II Specialty paths", "Level III design + reports", "Exam-day strategy"],
    track_link: "/nicet-exam-prep",
  },
  {
    id: "specialty",
    icon: Layers,
    title: "FDNY Specialty Certs",
    bullets: ["S-95 Visual inspection", "S-12 Citywide sprinkler", "S-13 Citywide standpipe", "F-53 One-way voice"],
    track_link: "/track/fdny-s95",
  },
  {
    id: "ce",
    icon: Briefcase,
    title: "Continuing Education",
    bullets: ["NFPA 72 code updates", "Voice/EVACS deep dive", "BMS integration", "Custom topics on request"],
    track_link: "/track/bms-elevator",
  },
];

const VALUE_PROPS = [
  { title: "Trained where it's installed", body: "Trevor has 15+ years of NYC field experience — WTC, Hudson Yards, JFK, NYU, JPMC. Your crews learn the code AND the job-site reality." },
  { title: "Flexible delivery", body: "On-site at your shop, remote/hybrid, or fully self-paced LMS access with cohort tracking. Scale from 5 to 500." },
  { title: "Audit-ready records", body: "Per-employee transcripts with verified hours, quiz scores, and credentials issued. CSV export for OSHA / FDNY / NICET audits." },
];

export default function CorporateTraining() {
  useDocumentMeta({
    title: "Corporate Fire Alarm Training — APM Fire Alarm Exam Prep",
    description: "Workforce training for fire alarm contractors, electrical shops, property managers, and engineering firms. OSHA, NFPA 72, S-98, NICET, FDNY specialty certs.",
    image: "/og/og-corporate.png",
    path: "/corporate",
  });
  useJsonLd([
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Corporate Training", url: "/corporate" },
    ]),
    faqSchema([
      { q: "Do you offer volume / contractor pricing?",
        a: "Yes. Pricing scales with team size and selected programs. Send an inquiry below and we'll respond with a custom proposal within 1 business day." },
      { q: "Can training happen at our shop or on-site?",
        a: "Yes — on-site, remote/hybrid, or fully self-paced LMS access with cohort tracking. We accommodate 5 to 500 person teams." },
      { q: "Do you provide audit-ready training records?",
        a: "Yes. Every employee gets a transcript with verified hours, quiz scores, and credentials issued. CSV export available for OSHA / FDNY / NICET audits." },
      { q: "Which programs are most popular for contractors?",
        a: "OSHA 30 + Fire Alarm Fundamentals for new hires, FDNY S-98 prep for inspectors, NICET Level 1-3 for senior technicians, and FDNY Specialty certs (S-95, S-12, S-13, F-53) for NYC-focused crews." },
    ]),
  ]);

  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    team_size: "1-5",
    interests: [],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleInterest = (id) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(id) ? f.interests.filter((x) => x !== id) : [...f.interests, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.interests.length === 0) {
      toast.error("Please select at least one training topic.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/corporate-inquiry", form);
      setSubmitted(true);
      toast.success("Inquiry received — Trevor will reach out within 1 business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send inquiry — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main data-testid="corporate-training-page">
        {/* Hero */}
        <section className="bg-zinc-950 text-white">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
            <div className="overline text-zinc-400 mb-3"><span className="red-bar" />Workforce training</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]" data-testid="corp-headline">
              Train your whole crew.<br/>
              <span className="text-red-500">Pass NICET + FDNY at scale.</span>
            </h1>
            <p className="text-zinc-300 mt-6 max-w-2xl text-lg leading-relaxed">
              Built for fire-alarm contractors, electrical shops, property-management firms,
              and engineering houses. Get your team certified faster — without losing them to a 6-month classroom course.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-10 max-w-3xl">
              {[
                { n: "5-500", l: "Team size we serve" },
                { n: "6", l: "Training programs" },
                { n: "On-site", l: "or remote / hybrid" },
                { n: "Volume", l: "Discounts available" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-red-600 pl-3">
                  <div className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{s.n}</div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why us */}
        <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-20" data-testid="corp-value-props">
          <div className="overline mb-3"><span className="red-bar" />Why contractors choose APM</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-10">
            Built by a field instructor, not a corporate trainer.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {VALUE_PROPS.map((vp, i) => (
              <div key={i} className="card-tech p-6" data-testid={`corp-vp-${i}`}>
                <div className="font-heading text-xl font-bold tracking-tight mb-2">{vp.title}</div>
                <p className="text-sm text-zinc-700 leading-relaxed">{vp.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Offerings grid */}
        <section className="bg-zinc-50 py-20" data-testid="corp-offerings">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="overline mb-3"><span className="red-bar" />Training programs</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">
              Pick the programs that fit your roster.
            </h2>
            <p className="text-sm text-zinc-600 mb-10 max-w-2xl">
              Mix and match — most contractors run OSHA + Fundamentals for new hires, then NICET + specialty tracks for senior techs.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {OFFERINGS.map((o) => {
                const Icon = o.icon;
                return (
                  <div key={o.id} className="card-tech p-6 hover:shadow-md transition-shadow" data-testid={`corp-offering-${o.id}`}>
                    <Icon className="w-6 h-6 text-red-600 mb-3" />
                    <h3 className="font-heading text-lg font-bold tracking-tight mb-3">{o.title}</h3>
                    <ul className="space-y-1.5 mb-4">
                      {o.bullets.map((b, i) => (
                        <li key={i} className="text-sm flex items-start gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-red-600 mt-1 flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    {o.track_link && (
                      <Link to={o.track_link} className="text-xs font-mono uppercase tracking-widest text-red-600 hover:text-red-700 inline-flex items-center gap-1" data-testid={`corp-offering-${o.id}-link`}>
                        Sample track <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Inquiry form */}
        <section className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-20" data-testid="corp-inquiry-section">
          {submitted ? (
            <div className="text-center py-16" data-testid="corp-success">
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">
                Thanks — we've got it.
              </h2>
              <p className="text-zinc-700 max-w-md mx-auto leading-relaxed mb-6">
                Trevor will reach out within 1 business day with a custom proposal. Check your inbox for our acknowledgement email.
              </p>
              <Link to="/curriculum"><button className="btn-outline">Browse curriculum</button></Link>
            </div>
          ) : (
            <>
              <div className="overline mb-3"><span className="red-bar" />Get a custom proposal</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">
                Tell us about your team.
              </h2>
              <p className="text-sm text-zinc-600 mb-8 max-w-2xl">
                We'll send back a tailored proposal within 1 business day. Pricing depends on team size,
                programs, and delivery format — no surprise fees.
              </p>
              <form onSubmit={submit} className="card-tech p-6 sm:p-8 space-y-5" data-testid="corp-inquiry-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">Company name *</span>
                    <input
                      type="text" required value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      className="mt-1 w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm bg-white"
                      data-testid="corp-company-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">Contact name *</span>
                    <input
                      type="text" required value={form.contact_name}
                      onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                      className="mt-1 w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm bg-white"
                      data-testid="corp-contact-input"
                    />
                  </label>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">Email *</span>
                    <input
                      type="email" required value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1 w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm bg-white"
                      data-testid="corp-email-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">Phone (optional)</span>
                    <input
                      type="tel" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(212) 555-0123"
                      className="mt-1 w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm bg-white"
                      data-testid="corp-phone-input"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">Team size *</span>
                  <select
                    required value={form.team_size}
                    onChange={(e) => setForm({ ...form, team_size: e.target.value })}
                    className="mt-1 w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm bg-white"
                    data-testid="corp-team-size-select"
                  >
                    <option value="1-5">1-5 people</option>
                    <option value="6-20">6-20 people</option>
                    <option value="21-50">21-50 people</option>
                    <option value="51-100">51-100 people</option>
                    <option value="100+">100+ people</option>
                  </select>
                </label>
                <div>
                  <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">Topics of interest * (pick any)</span>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {OFFERINGS.map((o) => {
                      const picked = form.interests.includes(o.id);
                      return (
                        <button
                          type="button" key={o.id}
                          onClick={() => toggleInterest(o.id)}
                          className={`text-left p-3 border-2 text-sm transition-colors ${picked ? "border-red-600 bg-red-50" : "border-zinc-200 hover:border-red-300"}`}
                          data-testid={`corp-interest-${o.id}`}
                        >
                          <div className="flex items-center gap-2">
                            {picked && <CheckCircle2 className="w-4 h-4 text-red-600 flex-shrink-0" />}
                            <span className="font-semibold">{o.title}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <label className="block">
                  <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">Tell us more (optional)</span>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={4}
                    maxLength={2000}
                    placeholder="Timeline, location, current cert status, anything we should know…"
                    className="mt-1 w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm bg-white"
                    data-testid="corp-message-input"
                  />
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full sm:w-auto justify-center"
                  data-testid="corp-submit-btn"
                >
                  {submitting ? "Sending…" : "Send inquiry"} <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-[11px] font-mono text-zinc-500">
                  By submitting, you agree to receive a follow-up email from Trevor at APM Fire Alarm Exam Prep. We never share contact info.
                </p>
              </form>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
